import Text from "../../../Text";
import {
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../../ui/dialog.tsx";
import { useEffect, type FC } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../../ui/form.tsx";
import { Input } from "../../../ui/input.tsx";
import { Button } from "../../../ui/button.tsx";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Link, useLocation, useNavigate } from "react-router";
import { signInUser, userState } from "@/store/userState.ts";
import { signInWithRedirect } from "aws-amplify/auth";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

const formSchema = z.object({
  email: z.email({ error: "Email invalide" }),
  password: z.string().min(1),
});

const getMessageError = (
  userError: Error,
  t: TFunction<"translation", undefined>,
): string => {
  if (
    ["UserNotFoundException", "NotAuthorizedException"].includes(
      userError.message.split(":")[0],
    )
  )
    return t("invalid_email_or_password");

  if (["NetworkError"].includes(userError.message.split(":")[0]))
    return t("network_error");

  return userError.message;
};

const SignIn: FC = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: userData, error: userError, signUpSucceed } = userState();

  const transformedPathname = pathname.replace("/sign-in", "");

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const handleSubmit = (values: z.infer<typeof formSchema>) => {
    signInUser(values);
  };

  useEffect(() => {
    if (userData) {
      navigate("/");
    }
  }, [userData]);

  return (
    <DialogContent
      showCloseButton={false}
      className="w-[370px] overflow-y-auto"
    >
      <VisuallyHidden>
        <DialogTitle>Sign In</DialogTitle>
      </VisuallyHidden>
      <DialogHeader>
        <VisuallyHidden>
          <DialogDescription>Sign In here</DialogDescription>
        </VisuallyHidden>
        <img
          src="/svg/krunsh-logo.svg"
          alt="krunsh logo"
          height={36}
          width={120}
        />
        <Text fontWeight={600} fontSize={20} color="#44546F">
          {t("auth_title")}
        </Text>
        <Text fontWeight={400} fontSize={14} color="#44546F">
          {t("auth_subtitle")}
        </Text>
      </DialogHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <div className="gap-[12px] flex flex-col">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("email")}</FormLabel>
                  <FormControl>
                    <Input placeholder="johndoe@gmail.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("password")}</FormLabel>
                  <FormControl>
                    <Input type="password" placeholder="*********" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <Link to={`${transformedPathname}/reset-password`}>
            <Text
              fontWeight={400}
              fontSize={14}
              color="#44546F"
              className="mt-[4px] block cursor-pointer"
            >
              {t("forgot_password")}
            </Text>
          </Link>

          <Button
            type="submit"
            variant="primaryFilled"
            className="w-[100%] my-[24px]"
          >
            {t("sign_in")}
          </Button>

          <Text fontSize={14} fontWeight={400} color="#414651">
            {t("or_sign_in_with")}
          </Text>
          <button
            type="button"
            className="w-full flex items-center justify-center bg-[white] rounded-[4px] gap-[8px] border border-[#F1F2F4] mt-[8px] px-[16px] py-[8px]"
            onClick={() => {
              signInWithRedirect({ provider: "Google" });
            }}
          >
            <img
              src="/svg/google-icon.svg"
              height={24}
              width={24}
              alt="google icon"
            />
            <Text fontSize={14} fontWeight={600}>
              Google
            </Text>
          </button>

          <div className="flex items-center justify-center gap-[4px] mt-[24px] w-[100%]">
            <Text>{t("not_sign_up_yet")}</Text>

            <Link to={`${transformedPathname}/sign-up`}>
              <Text color="#0F62FE">{t("sign_up")}</Text>
            </Link>
          </div>
        </form>
      </Form>
      {userError &&
        userError.message.split(":")[0] !== "UserUnAuthenticatedException" && (
          <Alert variant="destructive">
            <AlertTitle>{t("auth_error_title")}</AlertTitle>
            <AlertDescription>{getMessageError(userError, t)}</AlertDescription>
          </Alert>
        )}
      {signUpSucceed && (
        <Alert variant="destructive">
          <AlertTitle>{t("email_not_confirmed")}</AlertTitle>
          <AlertDescription>
            <Text>{t("email_confirmation_message")}</Text>
            <Link to="/explorer/confirm" className="mt-[8px]">
              <Button>{t("confirm_email")}</Button>
            </Link>
          </AlertDescription>
        </Alert>
      )}
    </DialogContent>
  );
};

export default SignIn;
